import React from 'react';
import { Modal, Button, Row, Col, Table, Badge, Card } from 'react-bootstrap';
import { Return, getReturnSourceSalesDisplay } from '../../models/return';
import { SUPPORTED_CURRENCIES } from '../../models/organization';
import { formatExchangeRate } from '../../utils/formatExchangeRate';
import detailStyles from '../shared/layout/detailView.module.scss';

interface ReturnViewProps {
  show: boolean;
  onClose: () => void;
  returnObj: Return | null;
  referenceCurrency?: string;
  currency?: string;
}

function getApprovalStateInfo(state: string) {
  switch (state) {
    case 'pending':
      return { label: 'Pendiente', variant: 'warning' as const };
    case 'approved':
      return { label: 'Aprobado', variant: 'success' as const };
    case 'rejected':
      return { label: 'Rechazado', variant: 'danger' as const };
    default:
      return { label: 'Desconocido', variant: 'secondary' as const };
  }
}

function formatDate(date: any): string {
  if (!date) return 'N/A';

  try {
    let dateObj: Date;

    // Handle Firestore timestamp format
    if (date && typeof date === 'object') {
      // Check if it's a Firestore Timestamp with toDate method
      if (typeof date.toDate === 'function') {
        dateObj = date.toDate();
      }
      // Handle Firestore timestamp: { seconds: number, nanoseconds: number }
      else if ('seconds' in date) {
        dateObj = new Date((date as any).seconds * 1000);
      }
      // Handle Date object
      else if (date instanceof Date) {
        dateObj = date;
      }
      // Try to convert to Date
      else {
        dateObj = new Date(date);
      }
    }
    // Handle string or number
    else {
      dateObj = new Date(date);
    }

    if (isNaN(dateObj.getTime())) return 'N/A';

    return dateObj.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return 'N/A';
  }
}

const ReturnView: React.FC<ReturnViewProps> = ({
  show,
  onClose,
  returnObj,
  referenceCurrency = 'USD',
  currency = 'VES',
}) => {
  if (!returnObj) return null;

  const stateInfo = getApprovalStateInfo(returnObj.approvalState);
  const subtotal = returnObj.subtotal || 0;
  const storedIvaAmount = returnObj.ivaAmount || 0;
  const inferredIvaAmount = (returnObj.total || 0) - subtotal;
  const ivaAmount =
    Math.abs(subtotal + storedIvaAmount - (returnObj.total || 0)) > 0.01
      ? inferredIvaAmount
      : storedIvaAmount;
  const total = returnObj.total || 0;
  const exchangeRate = returnObj.exchangeRate || 1;
  const displayCurrency = referenceCurrency;
  const getCurrencySymbol = (code?: string): string => {
    if (!code) return '';
    const match = SUPPORTED_CURRENCIES.find(curr => curr.code === code);
    return match?.symbol || code;
  };
  const numberFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const referenceSymbol = getCurrencySymbol(displayCurrency);
  const convertedSymbol = getCurrencySymbol(currency);
  const formatMoney = (value: number) =>
    `${referenceSymbol ? `${referenceSymbol} ` : ''}${numberFormatter.format(
      Number(value) || 0
    )}`;
  const formatConvertedMoney = (value: number) =>
    `${convertedSymbol ? `${convertedSymbol} ` : ''}${numberFormatter.format(
      Number(value) || 0
    )}`;
  const sourceSalesLabel = getReturnSourceSalesDisplay(returnObj);

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      centered
      contentClassName={detailStyles.modalContent}
    >
      <Modal.Header closeButton className={detailStyles.modalHeader}>
        <Modal.Title>Detalles de la Devolución</Modal.Title>
      </Modal.Header>
      <Modal.Body className={detailStyles.modalBody}>
        <div className={detailStyles.hero}>
          <div>
            <div className={detailStyles.eyebrow}>
              {returnObj.returnNumber
                ? `Devolución #${returnObj.returnNumber}`
                : 'Devolución'}
            </div>
            <h3 className={detailStyles.title}>{returnObj.clientName}</h3>
            <div className={detailStyles.subtitle}>
              {returnObj.creatorSalesPerson || 'Sin vendedor'} ·{' '}
              {formatDate(returnObj.created)}
            </div>
            <div className={detailStyles.badgeRow}>
              <Badge bg={stateInfo.variant}>{stateInfo.label}</Badge>
              {sourceSalesLabel && (
                <Badge bg="secondary">Origen {sourceSalesLabel}</Badge>
              )}
              {returnObj.deleted && <Badge bg="danger">Eliminada</Badge>}
            </div>
          </div>
          <div className={detailStyles.totalPanel}>
            <span className={detailStyles.totalLabel}>Total devuelto</span>
            <span className={detailStyles.totalValue}>{formatMoney(total)}</span>
            <span className={detailStyles.convertedValue}>
              {formatConvertedMoney(total * exchangeRate)}
            </span>
            <div className={`${detailStyles.exchangeNote} mt-2`}>
              {referenceCurrency}→{currency} · {formatExchangeRate(exchangeRate)}:1
            </div>
          </div>
        </div>
        <Row className="mb-4">
          <Col md={6}>
            <Card className={detailStyles.sectionCard}>
              <Card.Header className={detailStyles.sectionHeader}>
                <h6 className={detailStyles.sectionTitle}>Información del Cliente</h6>
              </Card.Header>
              <Card.Body>
                <div className={detailStyles.infoGrid}>
                <div className={detailStyles.infoItem}>
                  <span className={detailStyles.label}>Referencia</span>
                  <span className={detailStyles.value}>
                    {returnObj.returnNumber
                      ? `Devolución #${returnObj.returnNumber}`
                      : returnObj.id}
                  </span>
                </div>
                <div className={detailStyles.infoItem}>
                  <span className={detailStyles.label}>Cliente</span>
                  <span className={detailStyles.value}>{returnObj.clientName}</span>
                </div>
                <div className={detailStyles.infoItem}>
                  <span className={detailStyles.label}>Vendedor</span>
                  <span className={detailStyles.value}>
                    {returnObj.creatorSalesPerson}
                  </span>
                </div>
                {sourceSalesLabel && (
                  <div className={detailStyles.infoItem}>
                    <span className={detailStyles.label}>Orden Original</span>
                    <span className={detailStyles.value}>{sourceSalesLabel}</span>
                  </div>
                )}
                <div className={detailStyles.infoItem}>
                  <span className={detailStyles.label}>Fecha de Creación</span>
                  <span className={detailStyles.value}>{formatDate(returnObj.created)}</span>
                </div>
                {returnObj.updated && (
                  <div className={detailStyles.infoItem}>
                    <span className={detailStyles.label}>Última Actualización</span>
                    <span className={detailStyles.value}>{formatDate(returnObj.updated)}</span>
                  </div>
                )}
                {returnObj.comment && (
                  <div className={detailStyles.infoItem}>
                    <span className={detailStyles.label}>Comentarios</span>
                    <span className={detailStyles.value}>{returnObj.comment}</span>
                  </div>
                )}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className={detailStyles.sectionCard}>
              <Card.Header className={detailStyles.sectionHeader}>
                <h6 className={detailStyles.sectionTitle}>Información Financiera</h6>
              </Card.Header>
              <Card.Body>
                <div className={`${detailStyles.exchangeNote} mb-3`}>
                  Tipo de cambio ({referenceCurrency}→{currency}):{' '}
                  {formatExchangeRate(exchangeRate)}:1
                </div>

                <div className={detailStyles.moneyGrid}>
                <div className={detailStyles.moneyItem}>
                  <span className={detailStyles.label}>Subtotal</span>
                  <div className={detailStyles.moneyValue}>{formatMoney(subtotal)}</div>
                  <div className={detailStyles.exchangeNote}>
                    {formatConvertedMoney(subtotal * exchangeRate)}
                  </div>
                </div>
                {ivaAmount > 0 && (
                  <div className={detailStyles.moneyItem}>
                    <span className={detailStyles.label}>Impuestos</span>
                    <div className={detailStyles.moneyValue}>{formatMoney(ivaAmount)}</div>
                    <div className={detailStyles.exchangeNote}>
                      {formatConvertedMoney(ivaAmount * exchangeRate)}
                    </div>
                  </div>
                )}
                <div className={`${detailStyles.moneyItem} ${detailStyles.moneyItemTotal}`}>
                  <span className={detailStyles.label}>Total</span>
                  <div className={detailStyles.moneyValue}>{formatMoney(total)}</div>
                  <div className={detailStyles.exchangeNote}>
                    {formatConvertedMoney(total * exchangeRate)}
                  </div>
                </div>
                {returnObj.paymentCurrency && (
                  <div className={detailStyles.moneyItem}>
                    <span className={detailStyles.label}>Moneda de Pago</span>
                    <span className={detailStyles.value}>
                      {returnObj.paymentCurrency}
                    </span>
                  </div>
                )}
                {returnObj.deleted && (
                  <div>
                    <Badge bg="danger">Eliminada</Badge>
                  </div>
                )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className={detailStyles.productsCard}>
          <Card.Header className={detailStyles.sectionHeader}>
            <h6 className={detailStyles.sectionTitle}>
              Productos ({returnObj.products.length})
            </h6>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover size="sm" className={detailStyles.productsTable}>
                <thead>
                  <tr>
                    <th>Venta origen</th>
                    <th>Imagen</th>
                    <th>Código</th>
                    <th>Nombre</th>
                    <th>Marca</th>
                    <th>Descripción</th>
                    <th>Precio ({displayCurrency})</th>
                    <th>Impuesto</th>
                    <th>Monto Impuesto</th>
                    <th>Cantidad</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {returnObj.products.map((product, index) => (
                    <tr key={index}>
                      <td>
                        {product.sourceSaleNumber
                          ? `#${product.sourceSaleNumber}`
                          : product.sourceSaleId || '-'}
                      </td>
                      <td>
                        {product.imageUrl ? (
                          <img
                            src={product.imageUrl}
                            alt={product.name}
                            className={detailStyles.productImage}
                          />
                        ) : (
                          <div className={detailStyles.imageFallback}>
                            N/A
                          </div>
                        )}
                      </td>
                      <td>{product.code}</td>
                      <td>{product.name}</td>
                      <td>{product.brand || '-'}</td>
                      <td>{product.description || '-'}</td>
                      <td>{formatMoney(product.price)}</td>
                      <td>
                        {typeof product.taxRate === 'number'
                          ? `${numberFormatter.format(product.taxRate)}%`
                          : product.taxId || '-'}
                      </td>
                      <td>
                        {typeof product.taxRate === 'number'
                          ? formatMoney(
                              product.price *
                                product.quantity *
                                (product.taxRate / 100)
                            )
                          : '-'}
                      </td>
                      <td>{product.quantity}</td>
                      <td>{formatMoney(product.price * product.quantity)}</td>
                    </tr>
                  ))}
                  {returnObj.products.length === 0 && (
                    <tr>
                      <td colSpan={11} className="text-center text-muted">
                        No hay productos en esta devolución
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReturnView;
